/**
 * Delivery Portal Store
 * Manages delivery portal state and permissions
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useApi } from '@/composables/useApi';

export const useDeliveryPortalStore = defineStore('deliveryPortal', () => {
  // State
  const loading = ref(false);
  const error = ref(null);
  const myAccess = ref(null);
  const currentSubsectionAccess = ref(null);

  // Admin state
  const config = ref(null);
  const availableSources = ref([]);
  const targetOptions = ref({
    users: [],
    groups: [],
    entities: []
  });
  const allRules = ref([]);
  const allRulesTotalCount = ref(0);

  // Getters
  const hasPortalAccess = computed(() => myAccess.value?.hasAccess === true);
  const accessibleSubsections = computed(() => myAccess.value?.subsections || []);

  // All configured subsections (from config endpoint)
  const allConfiguredSubsections = computed(() => {
    if (!config.value) return [];
    // Map to consistent format with slug, displayName, and sourceSubsection
    return config.value.map(sub => ({
      slug: sub.portal_slug,
      displayName: sub.display_name,
      sourceSubsection: sub.source_subsection,
      sourceSection: sub.source_section,
      id: sub.id
    }));
  });

  /**
   * Check if user can access a specific subsection
   */
  function canAccessSubsection(slug) {
    if (!myAccess.value?.subsections) return false;
    return myAccess.value.subsections.some(s => s.slug === slug);
  }

  /**
   * Get subsection access details by slug
   */
  function getSubsectionAccess(slug) {
    if (!myAccess.value?.subsections) return null;
    return myAccess.value.subsections.find(s => s.slug === slug);
  }

  /**
   * Check if user can access a tab within current subsection
   */
  function canAccessTab(tabKey) {
    if (!currentSubsectionAccess.value) return false;
    return currentSubsectionAccess.value.allowedTabs?.includes(tabKey);
  }

  /**
   * Check if user can perform an action within current subsection
   * @param {string} actionKey - The action to check (e.g. 'edit', 'approve')
   * @param {string|null} tabKey - Optional tab key for tab-scoped permissions
   */
  function canPerformAction(actionKey, tabKey = null) {
    if (!currentSubsectionAccess.value) return false;
    const allowed = currentSubsectionAccess.value.allowedActions;
    if (!allowed) return false;
    // Handle map format: { _all: [...], tabKey: [...] }
    if (allowed && typeof allowed === 'object' && !Array.isArray(allowed)) {
      if (allowed._all?.includes(actionKey)) return true;
      if (tabKey && allowed[tabKey]?.includes(actionKey)) return true;
      return false;
    }
    // Backward compat: plain array (shouldn't happen)
    if (Array.isArray(allowed)) return allowed.includes(actionKey);
    return false;
  }

  /**
   * Get hidden columns for a specific tab
   * Returns array of column keys that should be hidden
   */
  function getHiddenColumnsForTab(tabKey) {
    if (!currentSubsectionAccess.value?.hiddenColumns) return [];
    const hiddenColumns = currentSubsectionAccess.value.hiddenColumns;
    // Combine tab-specific and global hidden columns
    const tabHidden = hiddenColumns[tabKey] || [];
    const globalHidden = hiddenColumns['_global'] || [];
    return [...new Set([...tabHidden, ...globalHidden])];
  }

  // ============================================
  // Runtime API Methods
  // ============================================

  /**
   * Fetch current user's portal access
   */
  async function fetchMyAccess() {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const response = await api.get('/api/delivery-portal/my-access');
      myAccess.value = response.data;
      return response.data;
    } catch (err) {
      error.value = err.message;
      myAccess.value = null;
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch access for a specific subsection
   */
  async function fetchSubsectionAccess(slug) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const response = await api.get(`/api/delivery-portal/access/${slug}`);
      currentSubsectionAccess.value = response.data;
      return response.data;
    } catch (err) {
      error.value = err.message;
      currentSubsectionAccess.value = null;
      return null;
    } finally {
      loading.value = false;
    }
  }

  // ============================================
  // Admin API Methods
  // ============================================

  /**
   * Fetch portal configuration (admin)
   */
  async function fetchConfig() {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const response = await api.get('/api/delivery-portal/config');
      config.value = response.data?.subsections || [];
      availableSources.value = response.data?.availableSources || [];
      return response.data;
    } catch (err) {
      error.value = err.message;
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch target options for dropdowns (users, groups, entities)
   */
  async function fetchTargetOptions() {
    const api = useApi();
    try {
      const response = await api.get('/api/delivery-portal/targets');
      targetOptions.value = response.data || { users: [], groups: [], entities: [] };
      return response.data;
    } catch (err) {
      error.value = err.message;
      return null;
    }
  }

  /**
   * Fetch all access rules across all subsections (admin)
   */
  async function fetchAllRules() {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const response = await api.get('/api/delivery-portal/all-rules');
      allRules.value = response.data?.rules || [];
      allRulesTotalCount.value = response.data?.totalCount || 0;
      return response.data;
    } catch (err) {
      error.value = err.message;
      allRules.value = [];
      allRulesTotalCount.value = 0;
      return null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Add a new subsection (admin)
   */
  async function addSubsection(subsectionData) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const data = await api.post('/api/delivery-portal/subsections', subsectionData);
      // Refresh config
      await fetchConfig();
      return data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update a subsection (admin)
   */
  async function updateSubsection(id, subsectionData) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const data = await api.put(`/api/delivery-portal/subsections/${id}`, subsectionData);
      // Refresh config
      await fetchConfig();
      return data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Remove a subsection (admin)
   */
  async function removeSubsection(id) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      const data = await api.delete(`/api/delivery-portal/subsections/${id}`);
      // Refresh config
      await fetchConfig();
      return data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get tab permissions for a subsection (admin)
   */
  async function fetchTabPermissions(subsectionId) {
    const api = useApi();
    try {
      const response = await api.get(`/api/delivery-portal/subsections/${subsectionId}/tabs`);
      return response.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  }

  /**
   * Update tab permissions (admin)
   */
  async function updateTabPermissions(subsectionId, permissions) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      return await api.put(`/api/delivery-portal/subsections/${subsectionId}/tabs`, { permissions });
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get action permissions for a subsection (admin)
   */
  async function fetchActionPermissions(subsectionId) {
    const api = useApi();
    try {
      const response = await api.get(`/api/delivery-portal/subsections/${subsectionId}/actions`);
      return response.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  }

  /**
   * Update action permissions (admin)
   */
  async function updateActionPermissions(subsectionId, permissions) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      return await api.put(`/api/delivery-portal/subsections/${subsectionId}/actions`, { permissions });
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get data policies for a subsection (admin)
   */
  async function fetchDataPolicies(subsectionId) {
    const api = useApi();
    try {
      const response = await api.get(`/api/delivery-portal/subsections/${subsectionId}/policies`);
      return response.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    }
  }

  /**
   * Create a data policy (admin)
   */
  async function createDataPolicy(subsectionId, policyData) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      return await api.post(`/api/delivery-portal/subsections/${subsectionId}/policies`, policyData);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update a data policy (admin)
   */
  async function updateDataPolicy(policyId, policyData) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      return await api.put(`/api/delivery-portal/policies/${policyId}`, policyData);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Delete a data policy (admin)
   */
  async function deleteDataPolicy(policyId) {
    const api = useApi();
    loading.value = true;
    error.value = null;
    try {
      return await api.delete(`/api/delivery-portal/policies/${policyId}`);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Reset store state
   */
  function $reset() {
    loading.value = false;
    error.value = null;
    myAccess.value = null;
    currentSubsectionAccess.value = null;
    config.value = null;
    availableSources.value = [];
    targetOptions.value = { users: [], groups: [], entities: [] };
    allRules.value = [];
    allRulesTotalCount.value = 0;
  }

  return {
    // State
    loading,
    error,
    myAccess,
    currentSubsectionAccess,
    config,
    availableSources,
    targetOptions,
    allRules,
    allRulesTotalCount,

    // Getters
    hasPortalAccess,
    accessibleSubsections,
    allConfiguredSubsections,

    // Permission checks
    canAccessSubsection,
    getSubsectionAccess,
    canAccessTab,
    canPerformAction,
    getHiddenColumnsForTab,

    // Runtime methods
    fetchMyAccess,
    fetchSubsectionAccess,

    // Admin methods
    fetchConfig,
    fetchTargetOptions,
    fetchAllRules,
    addSubsection,
    updateSubsection,
    removeSubsection,
    fetchTabPermissions,
    updateTabPermissions,
    fetchActionPermissions,
    updateActionPermissions,
    fetchDataPolicies,
    createDataPolicy,
    updateDataPolicy,
    deleteDataPolicy,

    // Reset
    $reset
  };
});
